import { ConsoleLogger, noopLogger } from "../logger/types";
import { isValidURL } from "../util";
import { getOrCreateMainLogger } from "../logger/main";
import { StandardPromise } from "../types/types";
import { Transform as Stream } from "stream";
import * as fs from "fs";
import path from "path";
import { mkdirp } from "mkdirp";
import { net } from "electron";
const logger = getOrCreateMainLogger();

interface Headers {
	[key: string]: string;
}

export type DownloadOptions = {
	headers?: Headers;
	returnStream?: boolean;
	timeout?: number;
	retries?: number;
	logger?: ConsoleLogger;
};

export type DownloadResult = {
	data: Buffer;
	response: Electron.IncomingMessage;
};

export type StreamResult = {
	data: Stream;
	response: Electron.IncomingMessage;
};

/**
 * Called by the wrapper function `download()` below. download() does retries.
 */
const downloadPrivate = (url: string, options?: DownloadOptions) =>
	new Promise<DownloadResult | StreamResult>((resolve, reject) => {
		const { headers, timeout = 30000, returnStream, logger = noopLogger } = options || {};
		let netTimer: ReturnType<typeof setTimeout> | null = null;

		const logAndReject = (error: string) => {
			logger.debug(error);
			reject(error);
		};

		logger.debug(`download(): begin url=${url}`);

		if (!isValidURL(url)) {
			logAndReject(`download(): ${url} is illegally formatted.`);
			return;
		}

		let request: Electron.ClientRequest | null = null;

		// the net.request doesn't detect when there is no Internet connection. A timer can be used to protect against this.
		if (timeout) {
			netTimer = setTimeout(() => {
				// When timing out, we abort the existing transaction so that we don't create a memory leak
				if (request) request.abort();
				logAndReject(`download(): Timed out ${url} after ${timeout} milliseconds`);
			}, timeout);
		}

		try {
			request = net.request(url);
		} catch (error) {
			// net.request will throw an error in some cases (e.g. unsupported protocol used in URL)
			if (netTimer) clearTimeout(netTimer);
			logAndReject(`download(): Electron.Net error for url: ${url}. Error: ${error}`);
			return;
		}

		// This code is being used to manually transfer cookies from the main session. It should probably should be replaced with useSessionCookies from https://www.electronjs.org/docs/api/client-request
		if (headers) {
			const keys = Object.keys(headers);
			keys.forEach((key) => request && request.setHeader(key, headers[key]));
		}

		request.on("response", (response) => {
			if (netTimer) clearTimeout(netTimer);

			if (response.statusCode >= 400) {
				logAndReject(`download(): HTTP failure from ${url}. HTTP Status Code = ${response.statusCode}`);
				return;
			}

			const data = new Stream();
			response.on("data", (chunk) => {
				data.push(chunk);
			});

			response.on("error", (error: Error) =>
				logAndReject(`download(): Response from ${url} errored with ${error.message}.`)
			);

			response.on("aborted", () => logAndReject(`download(): Response from ${url} was aborted.`));

			response.on("end", () => {
				logger.debug(`download(): successful end url=${url}`);

				if (returnStream) {
					return resolve({ data: data, response });
				}
				const result = data.read();
				resolve({ data: result, response });
			});
		});

		request.on("error", (error) => {
			if (netTimer) clearTimeout(netTimer);
			return logAndReject(`download(): Request ${url} errored with ${error}.`);
		});

		request.on("abort", () => {
			// Typically abort is called from a timeout but theoretically it could be from external activity at the OS level
			if (netTimer) clearTimeout(netTimer);
			return logAndReject(`download(): Request ${url} was aborted.`);
		});

		// The Electron.net request is only initiated once end() is called.
		request.end();
	});

/**
 * Download an http asset from a URL. This uses the Electron.net library (https://www.electronjs.org/docs/api/net)
 * This library is important because it uses the same proxies that Chrome uses: those that are configured on the user's desktop.
 * In many enterprises this is essential and therefore we cannot use Node's http (or tools built on Node http such as superagent.)
 *
 *  404 and other status errors are caught correctly.
 *  ERR_INTERNET_DISCONNECTED error will be received if internet is disconnected.
 *  ERR_NAME_NOT_RESOLVED error will be received for a bad hostname.
 *  ERR_CONNECTION_REFUSED will be received when there is no server listening. This is not reliable through firewalls and proxies in which case a timeout is likely to trigger instead.
 *
 *  See request API: (https://www.electronjs.org/docs/api/client-request)
 *  See response API: (https://www.electronjs.org/docs/api/incoming-message)
 *
 * Note: Use of the NET library means that files are loaded by Chrome, which also means that they might not actually be downloaded
 * if Chrome has cached that file (e.g. a 304 response). This is generally the desired behavior as it allows download() to be
 * called indiscriminately without affecting performance. To force a download, set Cache-Control: no-cache in options.headers.
 *
 * @param {String|Object} url to download file from or options from (https://electronjs.org/docs/api/client-request)
 * @param {Object} [options] Optional extra functionality
 * @param {Object} [options.headers] - all keys in this object are used as headers, the value is used as the header value
 * @param {Boolean} [options.returnStream] if true, resolve to a Stream. Deprecated use `stream()` instead.
 * @param {number} [options.timeout=30000] Optional timeout in milliseconds. Defaults to 10 seconds. Set to null for no timeouts, such as for very large transactions. Note that TCP's default timeout for connection problems is 2 minutes.
 * @param {number} [options.retries] Optional number of retries (in addition to the initial attempt). Only an error in the final retry will result in a reject.
 * @param {number} [options.logger] Optional logger. If not provided then logger messages will be silently ignored via a noopLogger.
 *
 * @return {Promise<Buffer>} resolves to a Buffer of data from the URL. Use Buffer.toString() to convert to a string if necessary.
 */

export const download = async (url: string, options?: DownloadOptions): Promise<DownloadResult> => {
	const retries = options?.retries || 1;
	let error;
	// Initial attempt + X number of retries
	for (let i = 0; i < retries + 1; i++) {
		try {
			return (await downloadPrivate(url, options)) as DownloadResult;
		} catch (err) {
			error = err;
		}
	}
	// If we got here then we've failed on retries
	throw error;
};

/**
 * Download wrapper with a GoLang style return instead of throwing exceptions.
 */
export const downloadGoLang = async (url: string, options?: DownloadOptions): StandardPromise<DownloadResult> => {
	try {
		const result = await download(url, options);
		return { data: result };
	} catch (err: any) {
		return { err };
	}
};

/**
 * Same as download() except returns a typescript safe Stream type. Automatically sets options.returnStream to true.
 * @return {StandardPromise<Stream>} Resolves to a stream of data from the URL.
 */
export const stream = async (url: string, options?: DownloadOptions): StandardPromise<StreamResult> => {
	const opts = { ...options, returnStream: true };
	try {
		// Hacky typescript to force the return type to a Stream. We know it's a Stream because we passed in returnStream: true
		const result = (await download(url, opts)) as unknown as StreamResult;
		return { data: result };
	} catch (err: any) {
		return { err };
	}
};

/**
 * Download a file.
 *
 * The requested directory will be created if it doesn't already exist.
 *
 * @param {Object} params
 * @param {String} params.toPath - location to write file
 * @param {String} params.fromPath - url to download file data
 * @param {String} params.fileName - name of file to write
 * @param {ConsoleLogger} params.logger - Optional logger
 */
export const downloadFile = async (params: any = {}): StandardPromise<string> => {
	if (!fs.existsSync(params.toPath)) {
		mkdirp.sync(params.toPath);
	}
	const url = params.fromPath;
	params.fileName = params.fileName ?? url.substring(url.lastIndexOf("/") + 1);

	const fileName = path.join(params.toPath, params.fileName);
	try {
		const data = await download(url, { logger: params.logger });
		fs.writeFileSync(fileName, data.data as Buffer);
		logger.debug("download complete", fileName);
		return { data: fileName };
	} catch (err: any) {
		logger.error("Error downloading", fileName, err);
		return { err };
	}
};
